import React from "react";
import {
  FaCertificate,
  FaShieldAlt,
  FaRegFileAlt,
  FaUniversity,
  FaBalanceScale,
  FaUserFriends,
  FaBriefcase,
  FaFileAlt,
  FaGlobe,
  FaTachometerAlt,
} from "react-icons/fa";

/* Import all logos */
const importAll = (r) => r.keys().map(r);
const brandImages = importAll(
  require.context("../../assets/Clients", false, /\.(png|jpe?g|webp)$/)
).sort();

/* Information Categories */
const governmentCategories = [
  {
    title: "Benefits & Social development",
    icon: FaUserFriends,
    description:
      "This section focuses on enhancing citizen welfare by providing information on benefits, subsidies, and social development initiatives.",
  },
  {
    title: "Business & Self-employed",
    icon: FaBriefcase,
    description:
      "Details on trade infrastructure, regulations, and policies supporting businesses and self-employed individuals.",
  },
  {
    title: "Citizenship, Visa & Passports",
    icon: FaFileAlt,
    description:
      "Information related to citizenship, visa applications, and passport services for Indian and foreign nationals.",
  },
  {
    title: "Defence & Foreign affairs",
    icon: FaGlobe,
    description:
      "Insights into India's defence systems and foreign affairs essential to national security and diplomacy.",
  },
  {
    title: "Driving & Transport",
    icon: FaTachometerAlt,
    description:
      "Comprehensive information on driving licenses, transport regulations, and national infrastructure.",
  },
   {
    title: "More information ?",
    icon: FaCertificate,
    description:
      "Please check out our FAQ section for more details on various government services and information.",
  },
];

const BrandGallery = () => {
  return (
    <section className="py-12 sm:py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ===================== SECTION 1 ===================== */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-stretch">

          {/* Credentials */}
          <div className="bg-primary rounded-xl p-5 sm:p-6 lg:p-8 text-white shadow-xl flex flex-col justify-center">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 border-b border-white/30 pb-3">
              Legal Entities
            </h2>

            <div className="space-y-5">
              <Credential
                icon={<FaRegFileAlt className="text-xl text-blue-300" />}
                text={
                  <>
                    <strong>NITI Aayog</strong>, Government of India.
                  </>
                }
              />

              <Credential
                icon={<FaShieldAlt className="text-xl text-green-300" />}
                text={
                  <>
                    <strong>Ministry of Corporate Affairs,</strong>
                     Government of India.
                  </>
                }
              />

              <Credential
                icon={<FaCertificate className="text-xl text-yellow-300" />}
                text={
                  <>
                    <strong>ISO 9001:2015 Certified</strong> – Quality Management System.
                  </>
                }
              />

              <Credential
                icon={<FaBalanceScale className="text-xl text-purple-300" />}
                text={
                  <>
                    <strong>Ministry of Social Justice and Empowerment</strong>,
                    Government of India.
                  </>
                }
              />

              <Credential
                icon={<FaUniversity className="text-xl text-red-300" />}
                text={
                  <>
                    <strong>Income Tax Act Registered</strong> (Sections 12A & 80G),
                    Government of India.
                  </>
                }
              />

              <Credential
                icon={<FaBalanceScale className="text-xl text-purple-300" />}
                text={
                  <>
                    <strong>Registered under Act, 2001</strong>, Government of Andhra Pradesh.
                  </>
                }
              />
            </div>
          </div>

          {/* Logo Gallery */}
          <div className="bg-gray-50 rounded-xl p-5 sm:p-6 lg:p-8 shadow-xl">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 sm:gap-4">
              {brandImages.map((imageUrl, index) => {
                const cleanName = imageUrl
                  .split("/")
                  .pop()
                  .replace(/\.(png|jpe?g|webp)$/i, "");

                return (
                  <div
                    key={index}
                    className="bg-white rounded-lg p-3 shadow-md hover:shadow-lg transition transform hover:scale-105 border border-gray-100 aspect-square flex items-center justify-center"
                  >
                    <img
                      src={imageUrl}
                      alt={cleanName}
                      className="max-h-12 sm:max-h-14 md:max-h-16 object-contain grayscale hover:grayscale-0 transition duration-500"
                      loading="lazy"
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* ===================== SECTION 2 ===================== */}
        <div className="mt-14 sm:mt-16">
          <div className="flex flex-col items-center mb-10">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">
              Information Categories
            </h2>
            <div className="w-16 h-1 bg-primary mt-2" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {governmentCategories.map((category, index) => (
              <a
                key={index}
                href="/frequently-asked-questions"
                className="group block bg-white rounded-xl p-5 sm:p-6 shadow-md hover:shadow-xl transition border-t-4 border-primary relative"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 sm:p-3 rounded-xl bg-primary flex-shrink-0">
                    <category.icon className="text-lg sm:text-2xl text-white" />
                  </div>

                  <h3 className="text-base sm:text-lg font-bold text-gray-900">
                    {category.title}
                  </h3>
                </div>

                <p className="text-gray-600 text-xs sm:text-sm mt-3">
                  {category.description}
                </p>

                {/* Arrow – Desktop Only */}
                {index < 3 && (
                  <div className="hidden lg:flex absolute top-1/2 right-0 -mr-3 -translate-y-1/2 h-6 w-6 bg-white border border-primary rounded-full items-center justify-center">
                    <div className="h-0 w-0 border-t-4 border-b-4 border-l-4 border-t-transparent border-b-transparent border-l-primary" />
                  </div>
                )}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Primary Color */}
      <style jsx>{`
        .bg-primary {
          background-color: #0d47a1;
        }
        .border-primary {
          border-color: #0d47a1;
        }
      `}</style>
    </section>
  );
};

/* Reusable Credential Item */
const Credential = ({ icon, text }) => (
  <div className="flex items-start gap-3">
    <div className="p-2 bg-white/20 rounded-lg flex-shrink-0">{icon}</div>
    <p className="text-white/95 text-sm sm:text-base">{text}</p>
  </div>
);

export default BrandGallery;
